import { useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { describeApiError } from './errors';
import { queryString } from './hooks';
import type { QrDetail } from './types';
import { useOrgApi } from './useOrgApi';
import { isHttpsUrl } from './utils';


export function EditDestinationModal({ qr, open, onClose, onSaved }: { qr: QrDetail; open: boolean; onClose: () => void; onSaved: (next: QrDetail) => void }) {
  const client = useOrgApi();
  const [value, setValue] = useState(qr.targetUrl ?? '');
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);

  const trimmed = value.trim();
  const localError = trimmed && !isHttpsUrl(trimmed) ? 'La dirección no es válida. Usa una URL completa que empiece con https://.' : undefined;
  const unchanged = trimmed === (qr.targetUrl ?? '');

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const next = await client.patch<QrDetail>(
        `/v1/qrs/${encodeURIComponent(qr.code)}/destination${queryString({ domain: qr.domain !== 'q-r.cl' ? qr.domain : undefined })}`,
        { targetUrl: trimmed },
        { idempotencyKey: newIdempotencyKey() },
      );
      if (next?.destStatus === 'pending') {
        setPending(true);
        onSaved({ ...qr, ...next });
        return;
      }
      onSaved({ ...qr, ...next, targetUrl: next?.targetUrl ?? trimmed });
      onClose();
    } catch (err) {
      setStep('edit');
      setError(describeApiError(err, 'No pudimos guardar el destino.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Editar destino · ${qr.code}`}
      footer={
        pending ? (
          <Button onClick={onClose}>Entendido</Button>
        ) : step === 'edit' ? (
          <>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => setStep('confirm')} disabled={!trimmed || Boolean(localError) || unchanged}>
              Revisar cambio
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setStep('edit')}>Volver</Button>
            <Button onClick={() => void save()} loading={saving}>Guardar destino</Button>
          </>
        )
      }
    >
      {pending ? (
        <Banner tone="warning">
          Guardamos el destino, pero el verificador de reputación no respondió. Lo revisamos en segundo plano; si resulta peligroso, te avisamos y se bloquea.
        </Banner>
      ) : step === 'edit' ? (
        <>
          <Input label="Destino nuevo" type="url" inputMode="url" value={value} onChange={(e) => { setValue(e.target.value); setError(null); }} error={localError} placeholder="https://" />
          {error ? <Banner tone="error">{error}</Banner> : <p className="rounded-md bg-gray-50 p-2 text-xs text-gray-700">El cambio se aplica desde el próximo escaneo. El código y la imagen del QR no cambian.</p>}
        </>
      ) : (
        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-xs text-ink-muted">Antes</dt>
          <dd className="break-all text-ink-muted line-through">{qr.targetUrl || '—'}</dd>
          <dt className="text-xs text-ink-muted">Después</dt>
          <dd className="break-all font-medium">{trimmed}</dd>
        </dl>
      )}
    </Modal>
  );
}
